<template>
  <v-container fluid>
    <v-btn text @click="$router.back()">
      <v-icon left>mdi-arrow-left</v-icon> {{ $t("back") }}
    </v-btn>

    <h2 class="font-weight-bold my-4">{{ $t("update_product_details") }}</h2>

    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row dense>
        <v-col cols="12">
          <v-radio-group v-model="form.type" row disabled>
            <v-radio :label="$t('buy_single_item')" value="item"></v-radio>
            <v-radio :label="$t('buy_box_item')" value="box"></v-radio>
          </v-radio-group>
        </v-col>
        <template v-if="form.unit.toLowerCase() === 'quantity'">
          <v-col cols="12">
            <v-text-field
              v-model="form.total_quantity"
              :label="
                form.type === 'box'
                  ? $t('quantity_of_boxes')
                  : $t('total_quantity')
              "
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
        </template>

        <template v-else-if="form.unit.toLowerCase() === 'kg'">
          <v-col cols="12">
            <v-text-field
              v-model="form.total_quantity"
              :label="$t('total_kg')"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
        </template>

        <v-col
          cols="12"
          v-if="form.unit.toLowerCase() === 'quantity' && form.type === 'box'"
        >
          <v-text-field
            v-model="form.purchased_price"
            :label="$t('purchased_price_per_box')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.purchased_price_per_product"
            :label="$t('purchased_price_per_product')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col
          cols="12"
          v-if="form.unit.toLowerCase() === 'quantity' && form.type === 'box'"
        >
          <v-text-field
            v-model="form.selling_price_per_box"
            :label="$t('selling_price_per_box')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.selling_price"
            :label="$t('selling_price_per_product')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.expiry_date"
                :label="$t('expiry_date')"
                readonly
                dense
                outlined
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.expiry_date"
              no-title
              @input="menu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="form.notes"
            :label="$t('note')"
            required
            outlined
            dense
            auto-grow
          />
        </v-col>

        <v-col cols="12">
          <v-btn color="amber darken-2" :disabled="!valid" @click="submitForm">
            {{ $t("update") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import api from "@/services/axios";

export default {
  data() {
    return {
      valid: false,
      menu: false,
      form: {
        name: "",
        product_id: "",
        unit: "",
        total_quantity: "",
        purchased_price_per_product: "",
        selling_price: "",
        expiry_date: "",
        selling_price_per_box: "",
        purchased_price_per_box: "",
        type: "item",
        notes: "",
      },
      rules: {
        required: (v) => !!v || this.$t("field_required"),
      },
    };
  },

  mounted() {
    if (this.$route.params.id && this.$route.params.detailId) {
      this.form.product_id = this.$route.params.id;
      this.fetchProductUnit();
      this.fetchProductDetail();
    }
  },

  methods: {
    async fetchProductUnit() {
      try {
        const res = await api.get("/product/view/" + this.form.product_id);
        this.form.unit = res.data.data.unit || "quantity";
      } catch (e) {
        console.error("Failed to fetch product unit", e);
      }
    },

    onUnitUpdate() {
      if (this.form.unit.toLowerCase() === "kg") {
        this.form.type = "item";
      }
    },

    async fetchProductDetail() {
      try {
        const res = await api.get(
          "/product/detail/view/" + this.$route.params.detailId
        );
        Object.assign(this.form, res.data.data);
        if (this.form.unit == "kg") {
          this.onUnitUpdate();
        }
      } catch (e) {
        console.error("Failed to fetch detail", e);
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;

      const formData = new FormData();
      if (this.form.total_quantity)
        formData.append("total_quantity", this.form.total_quantity);
      if (this.form.purchased_price_per_product)
        formData.append(
          "purchased_price_per_product",
          this.form.purchased_price_per_product
        );
      if (this.form.selling_price)
        formData.append("selling_price", this.form.selling_price);
      if (this.form.expiry_date)
        formData.append("expiry_date", this.form.expiry_date);
      if (this.form.type) formData.append("type", this.form.type);
      if (this.form.notes) formData.append("notes", this.form.notes);
      if (this.form.unit.toLowerCase() === "quantity") {
        if (this.form.selling_price_per_box)
          formData.append(
            "selling_price_per_box",
            this.form.selling_price_per_box
          );
        if (this.form.purchased_price)
          formData.append("purchased_price", this.form.purchased_price);
      }

      try {
        const res = await api.post(
          `/product/detail/update/${this.$route.params.detailId}`,
          formData,
          {
            headers: {
              Accept: "application/json",
              "Accept-Language": "en",
            },
          }
        );

        const message = res.data.locale_key || res.data.message;
        const color = res.data.status ? "success" : "error";

        this.$root.$emit("show-snackbar", { message, color });

        if (res.data.status) this.$router.back();
      } catch (e) {
        console.error(e);
        this.$root.$emit("show-snackbar", {
          message: "Failed to update product detail",
          color: "error",
        });
      }
    },
  },
};
</script>
